import { create } from 'zustand';
import { OfficeLocationStore, FormInstance } from './types';
import { validationSchema } from '../utils';

export const useOfficeLocationStore = create<OfficeLocationStore>()((set, get) => ({
  // Initial state
  forms: {},
  isSubmitting: false,

  setIsSubmitting: (isSubmitting) => set({ isSubmitting: isSubmitting }),

  // Actions
  addForm: (form) =>
    set((state) => ({
      forms: {
        ...state.forms,
        [form.id]: form,
      },
    })),

  removeForm: (formId) =>
    set((state) => {
      const newForms = { ...state.forms };
      delete newForms[formId];
      return { forms: newForms };
    }),

  setFieldValue: ({ formId, field, value }) =>
    set((state) => {
      const form = state.forms[formId];
      if (!form) return state;

      return {
        forms: {
          ...state.forms,
          [formId]: {
            ...form,
            formData: {
              ...form.formData,
              [field]: value,
            },
          },
        },
      };
    }),

  setFieldError: ({ formId, field, error }) =>
    set((state) => {
      const form = state.forms[formId];
      if (!form) return state;

      return {
        forms: {
          ...state.forms,
          [formId]: {
            ...form,
            errors: {
              ...form.errors,
              [field]: error,
            },
          },
        },
      };
    }),

  clearFieldError: ({ formId, field }) =>
    set((state) => {
      const form = state.forms[formId];
      if (!form) return state;

      const newErrors = { ...form.errors };
      delete newErrors[field];

      return {
        forms: {
          ...state.forms,
          [formId]: {
            ...form,
            errors: newErrors,
          },
        },
      };
    }),

  clearAllErrors: () =>
    set((state) => ({
      forms: Object.keys(state.forms).reduce((acc, formId) => {
        acc[formId] = {
          ...state.forms[formId],
          errors: {},
        };
        return acc;
      }, {} as Record<string | number, FormInstance>),
    })),

  validateAllForms: () => {
    const forms = get().forms;
    const formIds = Object.keys(forms);

    if (formIds.length === 0) return true;

    const validateForm = (formId: string | number) => {
      const form = forms[formId];
      if (!form) return true;

      let isCurrentFormValid = true;

      Object.entries(validationSchema).forEach(([field, validator]) => {
        if (validator) {
          const validationResult = validator(form.formData);
          if (validationResult) {
            set((state) => {
              const form = state.forms[formId];
              if (!form) return state;

              return {
                forms: {
                  ...state.forms,
                  [formId]: {
                    ...form,
                    errors: {
                      ...form.errors,
                      [field]: validationResult,
                    },
                  },
                },
              };
            });
            isCurrentFormValid = false;
          }
        }
      });

      return isCurrentFormValid;
    };

    let isAllFormsValid = true;
    formIds.forEach((formId) => {
      const isCurrentFormValid = validateForm(formId);
      isAllFormsValid = isAllFormsValid && isCurrentFormValid;
    });

    return isAllFormsValid;
  },

  clearState: () => set({ forms: {}, isSubmitting: false }),
}));
